// src/pages/sow-draft/ProjectDetailsStep.jsx
//
// Step 2 of the Draft SOW wizard: template pick, project info, scope,
// optional proposal upload, pricing model fields (varies by contract
// type), and resource plan -- then "Generate SOW with AI". Split out of
// SowDraftPage.jsx.

import PropTypes from "prop-types";
import { ArrowLeft, Plus, Sparkles } from "lucide-react";
import { Button, DatePicker, FormField, Input, Select, SurfaceCard, dateRangeError } from "../../shared/ui";
import { CONTRACT_TYPES, formatCurrency } from "./sowDraftHelpers";
import ProposalUpload from "./ProposalUpload";
import MilestonesTable from "./MilestonesTable";
import ResourcesTable from "./ResourcesTable";
import GenerationProgressBar from "./GenerationProgressBar";

export default function ProjectDetailsStep({
  selectedOpp,
  contractType,
  templates,
  selectedTemplateId,
  onTemplateChange,
  projectDetails,
  onFieldChange,
  resourceTotals,
  milestoneTotals,
  grandTotal,
  onAddMilestone,
  onUpdateMilestone,
  onRemoveMilestone,
  onAddResource,
  onUpdateResource,
  onRemoveResource,
  jossLoading,
  onFetchJoss,
  proposal,
  generating,
  genProgress,
  onGenerate,
  onBack,
  canCreateProject,
  onOpenNewProject,
}) {
  const canGenerate =
    !generating &&
    selectedTemplateId &&
    projectDetails.projectContext &&
    projectDetails.inScope &&
    projectDetails.outOfScope;

  return (
    <SurfaceCard>
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h2 className="text-base font-semibold text-ink">Project Details</h2>
          <p className="text-sm text-muted">
            <strong className="text-ink">{selectedOpp.account_name}</strong> · Contract:{" "}
            <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-brand-50 text-brand-600">{contractType}</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canCreateProject && (
            <Button variant="secondary" onClick={onOpenNewProject}>
              <Plus size={14} /> New Project
            </Button>
          )}
          <Button variant="secondary" onClick={onBack}>
            <ArrowLeft size={14} /> Back
          </Button>
        </div>
      </div>

      {generating && (
        <GenerationProgressBar
          section={genProgress.section}
          percent={genProgress.percent}
          startedAt={genProgress.startedAt}
          sectionCount={genProgress.total}
        />
      )}

      <div className="max-w-md mb-6">
        <FormField label="Template" required>
          <Select
            options={[
              { value: "", label: "Select…" },
              // Real template list items are {template_id, name, contract_type}
              // only -- no section count available to show.
              ...templates.map((t) => ({ value: String(t.template_id), label: t.name })),
            ]}
            value={selectedTemplateId}
            onChange={(e) => onTemplateChange(e.target.value)}
          />
        </FormField>
      </div>

      <h3 className="text-sm font-semibold text-ink mb-3">Timeline</h3>
      <div className="grid grid-cols-2 gap-3 mb-6">
        <FormField label="Start Date">
          <DatePicker
            value={projectDetails.startDate}
            max={projectDetails.endDate || undefined}
            onChange={(iso) => onFieldChange("startDate", iso)}
          />
        </FormField>
        {/* min couples the two fields: an end date before the start reached
            the generated SOW verbatim before this existed. */}
        <FormField label="End Date" errorText={dateRangeError(projectDetails.startDate, projectDetails.endDate)}
          error={Boolean(dateRangeError(projectDetails.startDate, projectDetails.endDate))}>
          <DatePicker
            value={projectDetails.endDate}
            min={projectDetails.startDate || undefined}
            onChange={(iso) => onFieldChange("endDate", iso)}
          />
        </FormField>
      </div>

      <h3 className="text-sm font-semibold text-ink mb-3">Scope Details (Required)</h3>
      <div className="grid grid-cols-2 gap-3 mb-6">
        <div className="col-span-2">
          <FormField label="Project Context" required>
            <textarea
              className="shui-input"
              rows={4}
              value={projectDetails.projectContext}
              onChange={(e) => onFieldChange("projectContext", e.target.value)}
              placeholder="Background, needs…"
            />
          </FormField>
        </div>
        <FormField label="In Scope" required>
          <textarea className="shui-input" rows={4} value={projectDetails.inScope} onChange={(e) => onFieldChange("inScope", e.target.value)} />
        </FormField>
        <FormField label="Out of Scope" required>
          <textarea className="shui-input" rows={4} value={projectDetails.outOfScope} onChange={(e) => onFieldChange("outOfScope", e.target.value)} />
        </FormField>
        <FormField label="Assumptions">
          <textarea className="shui-input" rows={3} value={projectDetails.assumptions} onChange={(e) => onFieldChange("assumptions", e.target.value)} />
        </FormField>
        <FormField label="Deliverables">
          <textarea className="shui-input" rows={3} value={projectDetails.deliverables} onChange={(e) => onFieldChange("deliverables", e.target.value)} />
        </FormField>
      </div>

      <h3 className="text-sm font-semibold text-ink mb-3">Proposal Documents (Optional)</h3>
      <div className="mb-6">
        <ProposalUpload
          files={proposal.files}
          onFilesChange={proposal.onFilesChange}
          parsed={proposal.parsed}
          uploading={proposal.uploading}
          error={proposal.error}
          onDismissError={proposal.onDismissError}
          onParse={proposal.onParse}
          acceptedTypes={proposal.acceptedTypes}
        />
      </div>

      {contractType === CONTRACT_TYPES.FIXED_BID && (
        <div className="mb-6">
          <MilestonesTable
            milestones={projectDetails.milestones}
            totals={milestoneTotals}
            grandTotal={grandTotal}
            onAdd={onAddMilestone}
            onUpdate={onUpdateMilestone}
            onRemove={onRemoveMilestone}
          />
        </div>
      )}

      {contractType === CONTRACT_TYPES.MANAGED && (
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-ink mb-3">Service Agreement</h3>
          <div className="grid grid-cols-3 gap-3">
            <FormField label="Monthly Fee ($)">
              <Input
                type="number"
                value={projectDetails.monthlyFee}
                onChange={(e) => onFieldChange("monthlyFee", parseFloat(e.target.value) || 0)}
              />
            </FormField>
            <FormField label="Duration (Months)">
              <Input
                type="number"
                value={projectDetails.duration}
                onChange={(e) => onFieldChange("duration", parseInt(e.target.value) || 1)}
              />
            </FormField>
            <FormField label="Total">
              <p className="shui-input bg-gray-50 font-semibold text-ink flex items-center">{formatCurrency(grandTotal)}</p>
            </FormField>
          </div>
        </div>
      )}

      <div className="mb-6">
        <ResourcesTable
          resources={projectDetails.resources}
          totals={resourceTotals}
          hasSfOpportunityId={Boolean(selectedOpp?.sf_opportunity_id)}
          jossLoading={jossLoading}
          onAdd={onAddResource}
          onFetchJoss={onFetchJoss}
          onUpdate={onUpdateResource}
          onRemove={onRemoveResource}
        />
      </div>

      <div className="flex justify-end">
        <Button size="lg" onClick={onGenerate} disabled={!canGenerate} loading={generating}>
          <Sparkles size={16} /> {generating ? "Generating…" : "Generate SOW with AI"}
        </Button>
      </div>
    </SurfaceCard>
  );
}

ProjectDetailsStep.propTypes = {
  selectedOpp: PropTypes.object.isRequired,
  contractType: PropTypes.string.isRequired,
  templates: PropTypes.array.isRequired,
  selectedTemplateId: PropTypes.string.isRequired,
  onTemplateChange: PropTypes.func.isRequired,
  projectDetails: PropTypes.object.isRequired,
  onFieldChange: PropTypes.func.isRequired,
  resourceTotals: PropTypes.object.isRequired,
  milestoneTotals: PropTypes.object.isRequired,
  grandTotal: PropTypes.number.isRequired,
  onAddMilestone: PropTypes.func.isRequired,
  onUpdateMilestone: PropTypes.func.isRequired,
  onRemoveMilestone: PropTypes.func.isRequired,
  onAddResource: PropTypes.func.isRequired,
  onUpdateResource: PropTypes.func.isRequired,
  onRemoveResource: PropTypes.func.isRequired,
  jossLoading: PropTypes.bool.isRequired,
  onFetchJoss: PropTypes.func.isRequired,
  proposal: PropTypes.object.isRequired,
  generating: PropTypes.bool.isRequired,
  genProgress: PropTypes.object.isRequired,
  onGenerate: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  canCreateProject: PropTypes.bool.isRequired,
  onOpenNewProject: PropTypes.func.isRequired,
};
